import { ByProductCode } from '@upright/common/model/input/products/types';
import {
  getItemList,
  getKTEntity,
  getKTImpact,
  getKTValueSet,
} from '@upright/common/model/output/scores/key-takeaways';
import { friendlyImpactTitle } from '../../../../../utils';
import { predefinedValueSets } from '../../../../input/value-sets/value-sets';
import {
  pickShowcaseCategory,
  SHOWCASE_EXCLUDED_NEGATIVE_CATEGORIES,
  SHOWCASE_EXCLUDED_POSITIVE_CATEGORIES,
} from '../showcase-categories';
import { KeyTakeawayType } from '../types';
import {
  CompanyKeyTakeaway,
  CompanyKeyTakeawayBestValueSet,
  CompanyKeyTakeawayBiggestNegative,
  CompanyKeyTakeawayBiggestPositive,
  CompanyKeyTakeawayHowToImprove,
} from './types-company-key-takeaway';

export interface CompanyKeyTakeawayProps {
  takeaway: CompanyKeyTakeaway;
  productNamesByCode: ByProductCode<string>;
  companyName: string | undefined;
  productCount: number;
}

// OA: HOX! This file is full of linting errors. Handle them if you modify this file after the Company Platform crunch.
/* eslint-disable */

export function getCompanyBiggestPositiveTakeaway(props: CompanyKeyTakeawayProps): string {
  if (props.takeaway.type !== KeyTakeawayType.BIGGEST_POSITIVE) {
    return ``;
  }

  const takeaway: CompanyKeyTakeawayBiggestPositive = props.takeaway;

  const impactCategories = takeaway.impactCategories;

  const friendlyImpactCategories = impactCategories.map(d => friendlyImpactTitle(d.impactCategory));
  const showcaseImpact = pickShowcaseCategory(impactCategories, SHOWCASE_EXCLUDED_POSITIVE_CATEGORIES);

  if (!showcaseImpact) {
    return `N/A`;
  }
  const showcaseProductCodes = showcaseImpact.contributingProducts.map(d => d.productCode);

  const showcaseIsLargest = showcaseImpact.impactCategory === impactCategories[0].impactCategory;
  const friendlyShowcase = friendlyImpactTitle(showcaseImpact.impactCategory);

  return `
      ${props.companyName} seems to create the most significant positive value in categories${' '}
      ${getKTImpact(friendlyImpactCategories[0])}, ${getKTImpact(friendlyImpactCategories[1])}, and${' '}
      ${getKTImpact(friendlyImpactCategories[2])}.${' '}
      ${
        showcaseIsLargest
          ? `The largest positive contribution comes from its ${getKTImpact(friendlyShowcase)} impact, which `
          : `The positive contribution in the ${getKTImpact(friendlyShowcase)} impact category `
      }
      is ${props.productCount > 3 ? 'mostly ' : ''}driven by its${' '}
      ${getItemList(showcaseProductCodes, props.productNamesByCode)}
      ${showcaseProductCodes.length > 1 ? `products` : `product`}.
    `;
}

export function getCompanyBiggestNegativeTakeaway(props: CompanyKeyTakeawayProps): string {
  if (props.takeaway.type !== KeyTakeawayType.BIGGEST_NEGATIVE) {
    return ``;
  }

  const takeaway: CompanyKeyTakeawayBiggestNegative = props.takeaway;

  const impactCategories = takeaway.impactCategories;

  const friendlyImpactCategories = impactCategories.map(d => friendlyImpactTitle(d.impactCategory));
  const showcaseImpact = pickShowcaseCategory(impactCategories, SHOWCASE_EXCLUDED_NEGATIVE_CATEGORIES);

  if (!showcaseImpact) {
    return `N/A`;
  }

  const showcaseProductCodes = showcaseImpact.contributingProducts.map(d => d.productCode);
  const showcaseIsLargest = showcaseImpact.impactCategory === impactCategories[0].impactCategory;
  const friendlyShowcase = friendlyImpactTitle(showcaseImpact.impactCategory);

  return `
  ${props.companyName} uses resources or causes negative impacts mostly in categories${' '}
  ${getKTImpact(friendlyImpactCategories[0])}, ${getKTImpact(friendlyImpactCategories[1])},
      and ${getKTImpact(friendlyImpactCategories[2])}.${' '}
      ${
        showcaseIsLargest
          ? `The largest negative impact of ${props.companyName} in category ${getKTImpact(friendlyShowcase)}`
          : `The negative contribution in the ${getKTImpact(friendlyShowcase)} impact category`
      }
      ${props.productCount > 3 ? `seems to be driven mostly` : `is driven`} by its${' '}
      ${getItemList(showcaseProductCodes, props.productNamesByCode)}
      ${showcaseProductCodes.length > 1 ? ` products` : ` product`}.
    `;
}

export function getCompanyBestValuesetTakeaway(props: CompanyKeyTakeawayProps): string {
  if (props.takeaway.type !== KeyTakeawayType.BEST_VALUE_SET) {
    return ``;
  }
  const takeaway: CompanyKeyTakeawayBestValueSet = props.takeaway;
  const valueSet = predefinedValueSets.find(d => d.id === takeaway.valueSetName);
  const valueSetText = valueSet?.keyTakeawayText;

  return `
      Currently the company’s net impact profile seems to best serve stakeholders who prioritize${' '}
      ${getKTValueSet(valueSetText)} over other priorities.
    `;
}

export function getCompanyHowToImproveTakeaway(props: CompanyKeyTakeawayProps): string {
  if (props.takeaway.type !== KeyTakeawayType.HOW_TO_IMPROVE) {
    return ``;
  }

  const takeaway: CompanyKeyTakeawayHowToImprove = props.takeaway;

  const improvementToShow = takeaway.improvements[0];

  const { productCode, netImpactContribution } = improvementToShow;
  const productName = props.productNamesByCode[productCode];

  return `
      To improve its net impact, a company can either grow its positive impacts, or reduce its negative impacts. For
      example, by doubling its ${getKTEntity(productCode, productName)} business, ${
        props.companyName
      } would improve its net impact ratio by
      ${(netImpactContribution * 100).toFixed(0)} percentage points.
    `;
}
